import { useLocalSearchParams } from 'expo-router'
import useOrderStep from 'features/order/hooks/useOrderStep'
import { useCallback } from 'react'

import {
  customizationPathname,
  getNextStepHref,
  isConfigFinished,
  NextStep,
  NextStepOptions,
} from 'features/order/utils/getNextStepHref'

import { useActiveDeal } from '@order/hooks/useDeal'
import { useConfigEndPath } from '@order/hooks/useLensCustomizations'
import { useOrderState } from '@order/hooks/useOrderState'
import { BundleType, SelectedItem, StepMode } from '@order/orderTypes'
import { StepCtx } from './useStepCtx'
import { getFlow } from '@order/utils/getFlow'
import useFlowName from '@order/hooks/useFlowName'

export type StepHrefFn = (
  id: number | string | null,
  item?: SelectedItem,
  opts?: Partial<NextStepOptions>
) => NextStep | undefined

const useStepHref = (ctx: StepCtx) => {
  const { config, refraction } = ctx
  const {
    stepId,
    orderItemId,
    mode,
    bundleType = 'base',
  } = useLocalSearchParams<{
    stepId: string
    orderItemId: string
    mode?: StepMode
    bundleType?: BundleType
  }>()
  const endPath = useConfigEndPath()

  const step = useOrderStep(stepId || '', ctx)
  const deal = useActiveDeal()
  const orderItems = useOrderState((s) => s.order.items)
  const flowName = useFlowName()
  const href: StepHrefFn = useCallback(
    (stepId, item, opts) => {
      if (!step) {
        return undefined
      }
      const val = getNextStepHref({
        config,
        orderItemId,
        stepId,
        states: step.states,
        values: step.values,
        flowName,
        item,
        mode,
        refraction,
        bundleType,
        endPath,
        ...opts,
      })
      return val
    },
    [step, refraction, endPath, mode, orderItemId, config, bundleType, flowName]
  )

  const nextFrameHref = (select: SelectedItem, next: NextStep | undefined) => {
    if (!next || !config) {
      return undefined
    }
    const flow = getFlow(config, flowName)
    const resolvedMode = mode ?? flow.stepModes[0]?.mode
    if (endPath === customizationPathname) {
      return undefined
    }
    const isFinished = isConfigFinished(next)
    if (!isFinished) {
      return undefined
    }

    const currentItems = orderItems.filter((i) => i.bundleType === bundleType)
    const totalFrames = deal?.frames || 1
    const currentFrames = currentItems.length ?? 1

    if (currentFrames >= totalFrames) {
      return undefined
    }

    const currentModeState = step?.states?.[resolvedMode]

    return {
      href: {
        pathname: '/frame/select',
        params: {
          bundleType,
        },
      },
      // nextItem,
      activeItemId: orderItemId,
      // FIXME:
      states: {
        ...step?.states,
        [resolvedMode]: currentModeState
          ? [...currentModeState, select]
          : [select],
      },
    }
  }

  return {
    step,
    nextFrameHref,
    href,
  }
}

export default useStepHref
